import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Bell, Check, Workflow, MessageSquare, AlertCircle } from 'lucide-react'
import { api, formatDateTime } from '@/lib/api'

interface Notif {
  id: number
  tipo: string
  titulo: string
  mensagem?: string
  url?: string
  icone?: string
  lida: boolean
  criada_em: string
}

const ICONES: Record<string, any> = {
  Workflow, MessageSquare, AlertCircle, Bell,
}

export default function NotificacoesBell() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const qc = useQueryClient()
  const nav = useNavigate()

  const { data: contador } = useQuery<{ nao_lidas: number }>({
    queryKey: ['notif-contador'],
    queryFn: async () => (await api.get('/notificacoes/contador').catch(() => ({ data: { nao_lidas: 0 } }))).data,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  })

  const { data: notifs = [] } = useQuery<Notif[]>({
    queryKey: ['notif-lista'],
    queryFn: async () => (await api.get('/notificacoes?lidas=false').catch(() => ({ data: [] }))).data,
    enabled: open,
  })

  const marcarLida = useMutation({
    mutationFn: async (id: number) => (await api.post(`/notificacoes/${id}/lida`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notif-contador'] })
      qc.invalidateQueries({ queryKey: ['notif-lista'] })
    },
  })

  const marcarTodas = useMutation({
    mutationFn: async () => (await api.post(`/notificacoes/marcar-todas-lidas`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notif-contador'] })
      qc.invalidateQueries({ queryKey: ['notif-lista'] })
    },
  })

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  function abrirNotif(n: Notif) {
    marcarLida.mutate(n.id)
    if (n.url) nav(n.url)
    setOpen(false)
  }

  const count = contador?.nao_lidas || 0

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className="p-2 rounded-md hover:bg-ink-100 text-ink-600 relative">
        <Bell size={18} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white rounded-full text-[10px] font-bold flex items-center justify-center">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-white rounded-lg shadow-xl border border-ink-200 z-50">
          <div className="px-4 py-3 border-b border-ink-200 flex items-center justify-between">
            <span className="font-semibold text-sm">Notificações</span>
            {count > 0 && (
              <button onClick={() => marcarTodas.mutate()} className="text-xs text-brand-600 hover:underline">
                Marcar todas lidas
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto divide-y divide-ink-100">
            {notifs.length === 0 && (
              <div className="p-6 text-center text-sm text-ink-500">
                Nenhuma notificação nova
              </div>
            )}
            {notifs.map(n => {
              const Ic = ICONES[n.icone || 'Bell'] || Bell
              return (
                <button key={n.id} onClick={() => abrirNotif(n)}
                  className="w-full p-3 text-left hover:bg-ink-50 flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center shrink-0">
                    <Ic className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-ink-800 truncate">{n.titulo}</div>
                    {n.mensagem && <div className="text-xs text-ink-500 truncate">{n.mensagem}</div>}
                    <div className="text-[10px] text-ink-400 mt-0.5">{formatDateTime(n.criada_em)}</div>
                  </div>
                  <Check className="w-4 h-4 text-ink-300 group-hover:text-brand-600 shrink-0 mt-1" />
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
